export type LegacyHtmlAttributes = Record<string, string>;

export const LEGACY_ATTRIBUTES_RUNTIME_ATTRIBUTE =
  'data-wysiwyg-editor-legacy-attributes';

// Class values remain selector hooks for TED and hotel stylesheets. They are
// encoded instead of mounted in the Admin visual DOM; the style policy below
// constrains inline declarations, not consumer-side computed CSS.
const SAFE_ATTRIBUTE_NAMES = new Set([
  'class',
  'dir',
  'id',
  'lang',
  'role',
  'title',
]);

const SAFE_STYLE_PROPERTIES = new Set([
  'background',
  'background-color',
  'border',
  'border-bottom',
  'border-bottom-color',
  'border-bottom-style',
  'border-bottom-width',
  'border-color',
  'border-left',
  'border-left-color',
  'border-left-style',
  'border-left-width',
  'border-right',
  'border-right-color',
  'border-right-style',
  'border-right-width',
  'border-style',
  'border-top',
  'border-top-color',
  'border-top-style',
  'border-top-width',
  'border-width',
  'clear',
  'color',
  'float',
  'font-size',
  'height',
  'left',
  'line-height',
  'margin',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'margin-top',
  'max-height',
  'max-width',
  'min-height',
  'min-width',
  'padding',
  'padding-bottom',
  'padding-left',
  'padding-right',
  'padding-top',
  'text-align',
  'text-transform',
  'top',
  'vertical-align',
  'white-space',
  'width',
]);

function isSafeAttributeName(name: string) {
  return (
    SAFE_ATTRIBUTE_NAMES.has(name) ||
    /^aria-[a-z0-9_.:-]+$/i.test(name) ||
    (/^data-[a-z0-9_.:-]+$/i.test(name) && !/^data-wysiwyg-editor-/i.test(name))
  );
}

function isSafeDirectionalValue(element: HTMLElement, value: string) {
  const match = /^(\d+(?:\.\d+)?)px$/.exec(value);
  const pixels = Number(match?.[1]);

  return (
    element.tagName === 'DIV' &&
    Boolean(match) &&
    pixels >= 0 &&
    pixels <= 10_000
  );
}

function isSafeStyleValue(
  element: HTMLElement,
  property: string,
  value: string
) {
  if (/(?:expression|javascript|url)\s*\(/i.test(value)) {
    return false;
  }

  if (property === 'left' || property === 'top') {
    return isSafeDirectionalValue(element, value);
  }

  return true;
}

function getSanitizedRuntimeLegacyAttributes(element: HTMLElement) {
  const encodedAttributes = element.getAttribute(
    LEGACY_ATTRIBUTES_RUNTIME_ATTRIBUTE
  );

  if (!encodedAttributes) {
    return {};
  }

  try {
    const attributeProbe = element.ownerDocument.createElement(element.tagName);

    Object.entries(
      renderSanitizedLegacyAttributes(JSON.parse(encodedAttributes))
    ).forEach(([name, value]) => attributeProbe.setAttribute(name, value));

    return getSanitizedLegacyAttributes(attributeProbe);
  } catch {
    return {};
  }
}

export function sanitizeLegacyStyle(element: HTMLElement) {
  const hasInsetDeclaration = [
    'inset',
    'inset-block',
    'inset-block-end',
    'inset-block-start',
    'inset-inline',
    'inset-inline-end',
    'inset-inline-start',
  ].some((property) => element.style.getPropertyValue(property));

  return Array.from(element.style)
    .filter(
      (property) =>
        SAFE_STYLE_PROPERTIES.has(property) &&
        (!hasInsetDeclaration || (property !== 'left' && property !== 'top'))
    )
    .flatMap((property) => {
      const value = element.style.getPropertyValue(property).trim();

      if (
        !value ||
        element.style.getPropertyPriority(property) ||
        !isSafeStyleValue(element, property, value)
      ) {
        return [];
      }

      return [`${property}: ${value};`];
    })
    .join(' ');
}

export function getSanitizedLegacyAttributes(element: HTMLElement) {
  const attributes: LegacyHtmlAttributes =
    getSanitizedRuntimeLegacyAttributes(element);

  Array.from(element.attributes).forEach((attribute) => {
    if (
      ((element.tagName === 'A' || element.tagName === 'BUTTON') &&
        attribute.name.startsWith('data-')) ||
      (element.tagName === 'A' &&
        !element.hasAttribute('href') &&
        attribute.name === 'id')
    ) {
      return;
    }

    if (attribute.name === 'style') {
      const style = sanitizeLegacyStyle(element);

      if (style) {
        attributes['style'] = style;
      }

      return;
    }

    if (isSafeAttributeName(attribute.name)) {
      attributes[attribute.name] = attribute.value;
    }
  });

  return attributes;
}

export function renderSanitizedLegacyAttributes(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === 'string' &&
        (entry[0] === 'style' || isSafeAttributeName(entry[0])) &&
        !/(?:expression|javascript|url)\s*\(|!important/i.test(entry[1])
    )
  );
}

export function renderRuntimeLegacyAttributes(value: unknown) {
  const attributes = renderSanitizedLegacyAttributes(value);

  return Object.keys(attributes).length
    ? { [LEGACY_ATTRIBUTES_RUNTIME_ATTRIBUTE]: JSON.stringify(attributes) }
    : {};
}

export function restoreSerializedLegacyAttributes(value: string) {
  if (!value.includes(LEGACY_ATTRIBUTES_RUNTIME_ATTRIBUTE)) {
    return value;
  }

  const document = new DOMParser().parseFromString(value, 'text/html');
  const existingIds = new Set(
    Array.from(document.body.querySelectorAll('[id]')).flatMap((element) => {
      const id = element.getAttribute('id');

      return id ? [id] : [];
    })
  );

  document.body
    .querySelectorAll(`[${LEGACY_ATTRIBUTES_RUNTIME_ATTRIBUTE}]`)
    .forEach((element) => {
      const legacyAttributes = getSanitizedRuntimeLegacyAttributes(
        element as HTMLElement
      );

      element.removeAttribute(LEGACY_ATTRIBUTES_RUNTIME_ATTRIBUTE);

      Object.entries(legacyAttributes).forEach(([name, attributeValue]) => {
        if (name === 'id') {
          if (!existingIds.has(attributeValue)) {
            element.setAttribute(name, attributeValue);
            existingIds.add(attributeValue);
          }

          return;
        }

        if (name === 'style') {
          const currentStyle = element.getAttribute('style');
          const mergedStyle = document.createElement('span');

          mergedStyle.setAttribute('style', attributeValue);

          if (currentStyle) {
            const editorStyle = document.createElement('span');

            editorStyle.setAttribute('style', currentStyle);
            Array.from(editorStyle.style).forEach((property) => {
              mergedStyle.style.setProperty(
                property,
                editorStyle.style.getPropertyValue(property)
              );
            });
          }

          if (mergedStyle.style.length) {
            element.setAttribute('style', mergedStyle.style.cssText);
          }

          return;
        }

        if (!element.hasAttribute(name)) {
          element.setAttribute(name, attributeValue);
        }
      });
    });

  return document.body.innerHTML;
}
